import { STAFF_TIMESHEET_REJECT_CATEGORIES, TIMESHEET_STATUSES } from '@hirebotiq-ops/core';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { OverridePanel, RejectPanel } from '../components/TimesheetActions.js';
import { TimesheetEntryEditor } from '../components/TimesheetEntryEditor.js';
import { Button } from '../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/timesheets')({
  component: AdminTimesheets,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

type StatusFilter = 'all' | (typeof TIMESHEET_STATUSES)[number];
type PanelState = { id: string; kind: 'reject' | 'override' } | null;

function AdminTimesheets() {
  const utils = trpc.useUtils();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [panel, setPanel] = useState<PanelState>(null);
  const [openEditorId, setOpenEditorId] = useState<string | null>(null);
  const list = trpc.timesheets.list.useQuery(status === 'all' ? {} : { status });

  const review = trpc.timesheets.review.useMutation({
    onSuccess: () => {
      utils.timesheets.list.invalidate();
      setPanel(null);
      toast.success('Stundenzettel bearbeitet.');
    },
    onError: (e) => toast.error(e.message),
  });
  const override = trpc.timesheets.overrideApprove.useMutation({
    onSuccess: () => {
      utils.timesheets.list.invalidate();
      setPanel(null);
      toast.success('Per Override freigegeben — Begründung protokolliert.');
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <AdminLayout>
      <div className="space-y-4">
        <header>
          <h1 className="text-3xl font-bold">Stundenzettel</h1>
          <p className="text-[var(--muted-foreground)]">
            Finale Freigabe nach Kundenbestätigung. Zettel ohne Einsatz gibst du direkt frei; alles
            andere braucht die Kundenstufe — Ausnahmen nur per dokumentiertem Override.
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Filter</CardTitle>
          </CardHeader>
          <CardContent className="max-w-xs">
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                {TIMESHEET_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Zeitraum</TableHead>
                  <TableHead>Stunden</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-[var(--muted-foreground)]">
                      Lade …
                    </TableCell>
                  </TableRow>
                )}
                {list.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-[var(--muted-foreground)]">
                      Keine Stundenzettel.
                    </TableCell>
                  </TableRow>
                )}
                {list.data?.map((t) => {
                  // Normale Freigabe nur aus `client_approved`; Zettel ohne
                  // Einsatz (kein Kunde) direkt aus `submitted` — spiegelt die
                  // atomaren Repo-Guards (OPS-61), damit die UI keine Aktionen
                  // anbietet, die der Server ablehnt.
                  const canApprove =
                    t.status === 'client_approved' ||
                    (t.status === 'submitted' && !t.assignmentClientId);
                  const canReject = [
                    'submitted',
                    'client_approved',
                    'client_corrected',
                    'disputed',
                  ].includes(t.status);
                  const canOverride =
                    !!t.assignmentClientId &&
                    ['submitted', 'client_corrected', 'disputed', 'rejected'].includes(t.status);
                  const showBillable = t.billable === false;
                  return (
                    <Fragment key={t.id}>
                      <TableRow>
                        <TableCell>
                          {[t.workerFirstName, t.workerLastName].filter(Boolean).join(' ') || '—'}
                        </TableCell>
                        <TableCell>{t.clientCompanyName ?? '—'}</TableCell>
                        <TableCell className="text-xs text-[var(--muted-foreground)]">
                          {t.periodStart} – {t.periodEnd}
                        </TableCell>
                        <TableCell>
                          {t.status === 'client_corrected' && t.correctedTotalHours ? (
                            <span>
                              <s className="text-[var(--muted-foreground)]">
                                {t.originalTotalHours}
                              </s>{' '}
                              → {t.correctedTotalHours}
                            </span>
                          ) : (
                            (t.totalHours ?? '—')
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <StatusBadge status={t.status} />
                            {showBillable && (
                              <span
                                className="text-xs text-[var(--muted-foreground)]"
                                title={t.nonBillableReason ?? undefined}
                              >
                                nicht abrechenbar
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            {canApprove && (
                              <Button
                                size="sm"
                                disabled={review.isPending}
                                onClick={() =>
                                  review.mutate({ timesheetId: t.id, action: 'approve' })
                                }
                              >
                                Freigeben
                              </Button>
                            )}
                            {canReject && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setPanel({ id: t.id, kind: 'reject' })}
                              >
                                Ablehnen
                              </Button>
                            )}
                            {canOverride && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-[var(--warning,#b45309)]"
                                onClick={() => setPanel({ id: t.id, kind: 'override' })}
                              >
                                Override …
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setOpenEditorId((curr) => (curr === t.id ? null : t.id))}
                            >
                              {openEditorId === t.id ? 'Zeilen & Zuschläge zu' : '📅 Zeilen & Zuschläge'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {openEditorId === t.id && (
                        <TableRow>
                          <TableCell colSpan={6} className="p-4 bg-[var(--muted)]/10">
                            <TimesheetEntryEditor
                              timesheetId={t.id}
                              periodStart={t.periodStart}
                              periodEnd={t.periodEnd}
                              timezone={t.timezone ?? 'Europe/Berlin'}
                              readOnly={true}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      {panel?.id === t.id && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            {panel.kind === 'reject' ? (
                              <RejectPanel
                                pending={review.isPending}
                                categories={STAFF_TIMESHEET_REJECT_CATEGORIES}
                                onCancel={() => setPanel(null)}
                                onSubmit={(category, notes) =>
                                  review.mutate({
                                    timesheetId: t.id,
                                    action: 'reject',
                                    category,
                                    notes,
                                  })
                                }
                              />
                            ) : (
                              <OverridePanel
                                pending={override.isPending}
                                onCancel={() => setPanel(null)}
                                onSubmit={(reason) =>
                                  override.mutate({ timesheetId: t.id, reason })
                                }
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
